import { useState } from 'react'
import { StatsPeriodSelect } from '@/components/StatsPeriodSelect'
import { Skeleton } from '@/components/ui/skeleton'
import { formatHmm } from '@/lib/flight-log-utils'
import { isPilotRole } from '@@api/shared'
import type { CrewFlightStats as CrewFlightStatsBuckets, FlightStatsPeriod } from '@@api/flightLog.shared'

// Stats column on the profile card, shared by the crew detail and own-profile
// layouts. All three numbers come from the crew member's submitted flight logs
// (see crew.getById): Flights is the log count, Flight/Block Time are h:mm
// sums. Block Time only applies to pilots. The pill at the top switches
// between the all time / this year / this month buckets the API already
// returned, so changing it never refetches.
export function CrewFlightStats({
  crew,
  loading = false,
}: {
  crew: {
    crewRole: string | null | undefined
    flightStats: CrewFlightStatsBuckets | undefined
  }
  loading?: boolean
}) {
  const [period, setPeriod] = useState<FlightStatsPeriod>('allTime')
  const totals = crew.flightStats?.[period]

  return (
    <div className="flex flex-col gap-2 w-[114px]">
      <StatsPeriodSelect value={period} onChange={setPeriod} />
      <div className="flex flex-col">
        {loading ? (
          <Skeleton className="h-7 w-8" />
        ) : (
          <p className="text-xl font-semibold">{totals?.totalFlights ?? 0}</p>
        )}
        <p className="text-xs text-content-primary-subtle leading-normal">Flights</p>
      </div>
      <div className="flex flex-col">
        {loading ? (
          <Skeleton className="h-7 w-14" />
        ) : (
          <p className="text-xl font-semibold">{formatHmm(totals?.totalFlightMinutes ?? 0)}</p>
        )}
        <p className="text-xs text-content-primary-subtle leading-normal">Flight Time</p>
      </div>
      {isPilotRole(crew.crewRole) && (
        <div className="flex flex-col">
          {loading ? (
            <Skeleton className="h-7 w-14" />
          ) : (
            <p className="text-xl font-semibold">{formatHmm(totals?.totalBlockMinutes ?? 0)}</p>
          )}
          <p className="text-xs text-content-primary-subtle leading-normal">Block Time</p>
        </div>
      )}
    </div>
  )
}
